import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { PagedResultDto } from '../../../models/table.model';
import {
  ParkingFinanceListRowDto,
  ParkingFinanceTableQuery,
  ParkingFinanceMarketingDashboardDto,
  CancelParkingFinancePayload,
  CancelParkingFinanceResponseDto,
  IssueFullTaxInvoiceFromFinancePayload,
  IssueFullTaxInvoiceFromFinanceResponseDto
} from '../../../models/finance/parking-finance.model';

@Injectable({ providedIn: 'root' })
export class ApiParkingFinanceService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  // ================= Normal Functions =================
  // (None)

  // ================= API Functions =================

  /** Fetch parking finance list for standard server-side paginated table */
  getParkingFinanceForTable(query: ParkingFinanceTableQuery): Observable<PagedResultDto<ParkingFinanceListRowDto>> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId: query.siteId,
      userId: query.userId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.startDate) params['startDate'] = query.startDate;
    if (query.endDate) params['endDate'] = query.endDate;
    if (query.searchField) params['searchField'] = query.searchField;
    if (query.searchText) params['searchText'] = query.searchText;
    if (query.feeType) params['feeType'] = query.feeType;
    if (query.feeName) params['feeName'] = query.feeName;
    if (query.status) params['status'] = query.status;
    if (query.minTotal && query.minTotal > 0) params['minTotal'] = query.minTotal;

    return this.apiMethodService.request<PagedResultDto<ParkingFinanceListRowDto>>(
      '/Backoffice/getParkingFinanceForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingFinanceForTable',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  /** Fetch parking finance dashboard values including summaries and recent items */
  getParkingFinanceMarketingDashboard(
    siteId: number,
    userId: number,
    startDate?: string,
    endDate?: string
  ): Observable<ParkingFinanceMarketingDashboardDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = { siteId, userId };
    if (startDate) params['startDate'] = startDate;
    if (endDate) params['endDate'] = endDate;

    return this.apiMethodService.request<ParkingFinanceMarketingDashboardDto>(
      '/Backoffice/getParkingFinanceMarketingDashboard',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingFinanceMarketingDashboard',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  /** Request cancellation of a parking finance entry */
  requestCancelParkingFinance(payload: CancelParkingFinancePayload): Observable<CancelParkingFinanceResponseDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<CancelParkingFinanceResponseDto>(
      '/Backoffice/requestCancelParkingFinance',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'requestCancelParkingFinance',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  /** Issue full tax invoice (RL) from an existing parking finance record */
  issueFullTaxInvoiceFromFinance(
    payload: IssueFullTaxInvoiceFromFinancePayload
  ): Observable<IssueFullTaxInvoiceFromFinanceResponseDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<IssueFullTaxInvoiceFromFinanceResponseDto>(
      '/Backoffice/issueFullTaxInvoiceFromFinance',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'issueFullTaxInvoiceFromFinance',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }
}
